import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Phone, Info } from 'lucide-react';
import Icon from '../components/Icon';
import ReviewsCarousel from '../components/ReviewsCarousel';
import useReveal from '../hooks/useReveal';
import {
  hero, badges, services, aboutPhysio, meetKate, gallery,
  benefits, pricing, faqs, arranging, contactCta, site,
} from '../mock/mock';
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from '../components/ui/accordion';

export default function Home() {
  useReveal();
  return (
    <div>
      {/* HERO */}
      <section className="relative">
        <div className="relative h-[86vh] min-h-[560px] hero-clip overflow-hidden">
          <img src={hero.image} alt="Veterinary physiotherapist caring for a horse" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/55" />
          <div className="relative h-full flex flex-col items-center justify-center text-center px-5 pb-16">
            <span className="eyebrow text-white/90 bg-white/15 backdrop-blur-sm border border-white/25 rounded-full px-5 py-2 mb-7 animate-fade-up">
              {hero.eyebrow}
            </span>
            <h1 className="text-white font-serif text-5xl md:text-7xl font-bold drop-shadow-lg max-w-4xl animate-fade-up" style={{ animationDelay: '0.1s' }}>
              {hero.title}
            </h1>
            {hero.subtitle ? (
              <p className="text-white/90 font-serif italic text-xl md:text-3xl mt-4 drop-shadow animate-fade-up" style={{ animationDelay: '0.2s' }}>
                {hero.subtitle}
              </p>
            ) : null}
            <Link
              to="/contact"
              className="mt-9 group inline-flex items-center gap-2 bg-white text-sage-700 hover:text-sage-800 font-semibold px-8 py-4 rounded-full shadow-xl hover:shadow-2xl hover:-translate-y-0.5 transition-all animate-fade-up"
              style={{ animationDelay: '0.3s' }}
            >
              {hero.cta}
              <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>

        {/* Intro */}
        <div className="max-w-3xl mx-auto px-5 py-16 text-center">
          {hero.intro.map((p, i) => (
            <p key={i} className="text-ink/75 leading-loose text-[1.05rem] mb-5 reveal">{p}</p>
          ))}
        </div>
      </section>

      {/* TRUST BADGES */}
      <section className="bg-cream py-12">
        <div className="max-w-[1100px] mx-auto px-5 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8">
          {badges.map((b, i) => (
            <div key={b.label} className="flex flex-col items-center text-center reveal" style={{ transitionDelay: `${i * 60}ms` }}>
              <div className="w-16 h-16 rounded-full bg-white shadow-sm flex items-center justify-center mb-3 transition-transform hover:scale-110">
                <Icon name={b.icon} size={26} className="text-sage-500" />
              </div>
              <span className="text-sm font-medium text-ink/80">{b.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-24">
        <div className="max-w-[1200px] mx-auto px-5">
          <div className="text-center max-w-2xl mx-auto mb-16 reveal">
            <p className="eyebrow text-sage-500 mb-3">{services.eyebrow}</p>
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-ink mb-4">{services.title}</h2>
            <p className="text-ink/60 text-lg">{services.subtitle}</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-7">
            {services.items.map((s, i) => (
              <div
                key={s.title}
                className="group bg-white border border-cream-dark rounded-2xl p-8 shadow-[0_2px_18px_rgba(0,0,0,0.04)] hover:shadow-xl hover:-translate-y-1 transition-all reveal"
                style={{ transitionDelay: `${(i % 3) * 80}ms` }}
              >
                <div className="w-14 h-14 rounded-xl bg-sage-50 flex items-center justify-center mb-6 transition-colors group-hover:bg-sage-100">
                  <Icon name={s.icon} size={26} className="text-sage-600" />
                </div>
                <h3 className="text-2xl font-serif text-ink mb-3">{s.title}</h3>
                <p className="text-ink/65 leading-relaxed mb-5">{s.text}</p>
                <Link to={s.to} className="inline-flex items-center gap-2 text-sage-600 font-semibold text-sm group/link">
                  More info
                  <ArrowRight size={16} className="transition-transform group-hover/link:translate-x-1" />
                </Link>
              </div>
            ))}
          </div>
          <div className="mt-12 flex items-start gap-4 bg-cream rounded-2xl p-6 max-w-4xl mx-auto reveal">
            <Info size={22} className="text-sage-500 shrink-0 mt-0.5" />
            <p className="text-ink/70 text-[0.95rem] leading-relaxed">{services.note}</p>
          </div>
        </div>
      </section>

      {/* REVIEWS */}
      <ReviewsCarousel />

      {/* ABOUT PHYSIO */}
      <section className="py-24">
        <div className="max-w-3xl mx-auto px-5">
          <h2 className="text-4xl md:text-5xl font-serif font-bold text-ink text-center mb-10 reveal">{aboutPhysio.title}</h2>
          {aboutPhysio.paragraphs.map((p, i) => (
            <p key={i} className="text-ink/70 leading-loose mb-6 reveal">{p}</p>
          ))}
        </div>
      </section>

      {/* MEET KATE */}
      <section className="bg-cream py-24">
        <div className="max-w-[1120px] mx-auto px-5">
          <div className="text-center max-w-2xl mx-auto mb-12 reveal">
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-ink mb-4">{meetKate.title}</h2>
            <p className="text-ink/65 text-lg">
              {meetKate.intro} You can discover more on the{' '}
              <Link to="/about" className="text-sage-600 font-semibold hover:underline">About</Link> page.
            </p>
          </div>
          <div className="bg-white rounded-3xl shadow-xl overflow-hidden grid md:grid-cols-2 reveal">
            <div className="h-72 md:h-auto">
              <img src={meetKate.image} alt={meetKate.name} className="w-full h-full object-cover" />
            </div>
            <div className="p-9 md:p-11 flex flex-col justify-center">
              <h3 className="text-3xl font-serif text-ink mb-1">{meetKate.name}</h3>
              <p className="text-sage-600 font-semibold mb-1">{meetKate.role}</p>
              <p className="text-ink/50 text-sm mb-5">{meetKate.creds}</p>
              <p className="text-ink/70 leading-relaxed mb-6">{meetKate.bio}</p>
              <Link to="/contact" className="inline-flex items-center gap-2 text-sage-600 font-semibold group w-fit">
                Book with Kate
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* GALLERY */}
      <section className="py-24">
        <div className="max-w-[1120px] mx-auto px-5">
          <div className="text-center max-w-2xl mx-auto mb-14 reveal">
            <p className="eyebrow text-sage-500 mb-3">{gallery.eyebrow}</p>
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-ink mb-4">{gallery.title}</h2>
            <p className="text-ink/60 text-lg">{gallery.subtitle}</p>
          </div>
          <div className="columns-2 md:columns-4 gap-4 [column-fill:_balance]">
            {gallery.images.map((img, i) => (
              <div key={i} className="mb-4 break-inside-avoid overflow-hidden rounded-xl shadow-sm reveal group">
                <img
                  src={img.src}
                  alt={img.alt}
                  className={`w-full object-cover transition-transform duration-700 group-hover:scale-105 ${i % 3 === 0 ? 'aspect-[3/4]' : 'aspect-square'}`}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT CTA BAND */}
      <section className="bg-sage-500 py-16">
        <div className="max-w-3xl mx-auto px-5 text-center text-white">
          <h2 className="text-3xl md:text-4xl font-serif mb-5">{contactCta.title}</h2>
          <p className="text-white/90 leading-relaxed mb-8">
            Call Kate on{' '}
            <a href={site.phoneHref} className="font-semibold underline underline-offset-4">{site.phone}</a>{' '}
            or click the button to fill in your details. We will get back to you as soon as possible.
          </p>
          <Link to="/contact" className="inline-flex items-center gap-2 bg-white text-sage-700 hover:text-sage-800 font-semibold px-8 py-3.5 rounded-full shadow-lg hover:-translate-y-0.5 transition-all">
            Contact Us <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="py-24">
        <div className="max-w-[1200px] mx-auto px-5">
          <div className="text-center max-w-3xl mx-auto mb-16 reveal">
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-ink mb-5">{benefits.title}</h2>
            <p className="text-ink/65 text-lg leading-relaxed">{benefits.intro}</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {benefits.items.map((b, i) => (
              <div key={b.title} className="group reveal" style={{ transitionDelay: `${i * 80}ms` }}>
                <div className="rounded-2xl overflow-hidden shadow-md mb-6 aspect-[4/3]">
                  <img src={b.image} alt={b.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <h3 className="text-2xl font-serif text-ink mb-3">{b.title}</h3>
                <p className="text-ink/65 leading-relaxed">{b.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="bg-cream py-24">
        <div className="max-w-[1100px] mx-auto px-5">
          <div className="text-center max-w-2xl mx-auto mb-14 reveal">
            <p className="eyebrow text-sage-500 mb-3">{pricing.eyebrow}</p>
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-ink mb-4">{pricing.title}</h2>
            <p className="text-ink/60 text-lg">{pricing.subtitle}</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {pricing.items.map((p, i) => (
              <div
                key={p.animal}
                className="bg-white rounded-2xl p-8 text-center shadow-sm border border-cream-dark hover:shadow-xl hover:-translate-y-1 transition-all reveal"
                style={{ transitionDelay: `${i * 70}ms` }}
              >
                <div className="w-14 h-14 rounded-full bg-sage-50 flex items-center justify-center mx-auto mb-5">
                  <Icon name={p.icon} size={26} className="text-sage-600" />
                </div>
                <h3 className="font-serif text-lg text-ink mb-3 min-h-[3.5rem] flex items-center justify-center">{p.animal}</h3>
                <p className="text-4xl font-serif font-bold text-sage-600">{p.price}</p>
                <p className="text-ink/50 text-sm mt-1">{p.unit}</p>
              </div>
            ))}
          </div>
          <div className="mt-10 space-y-4 max-w-3xl mx-auto">
            <div className="flex items-start gap-4 bg-white rounded-2xl p-6 shadow-sm reveal">
              <Icon name="HeartPulse" size={22} className="text-sage-500 shrink-0 mt-0.5" />
              <p className="text-ink/70 text-[0.95rem] leading-relaxed">{pricing.rehabNote}</p>
            </div>
            <div className="flex items-start gap-4 bg-white rounded-2xl p-6 shadow-sm reveal">
              <Info size={22} className="text-sage-500 shrink-0 mt-0.5" />
              <p className="text-ink/70 text-[0.95rem] leading-relaxed">{pricing.travel}</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24">
        <div className="max-w-3xl mx-auto px-5">
          <div className="text-center mb-12 reveal">
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-ink mb-4">{faqs.title}</h2>
            <p className="text-ink/60">
              If you have any other questions or would like to discuss our treatment options, please{' '}
              <Link to="/contact" className="text-sage-600 font-semibold hover:underline">contact us.</Link>
            </p>
          </div>
          <Accordion type="single" collapsible className="space-y-4 reveal">
            {faqs.items.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="bg-white border border-cream-dark rounded-xl px-6 shadow-sm">
                <AccordionTrigger className="text-left font-serif text-lg text-ink hover:no-underline py-5">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-ink/70 leading-relaxed pb-5">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* ARRANGING APPOINTMENT */}
      <section className="bg-cream py-24">
        <div className="max-w-[1120px] mx-auto px-5 grid md:grid-cols-2 gap-14 items-center">
          <div className="reveal">
            <h2 className="text-4xl font-serif font-bold text-ink mb-6">{arranging.title}</h2>
            {arranging.paragraphs.map((p, i) => (
              <p key={i} className="text-ink/70 leading-relaxed mb-5">{p}</p>
            ))}
            <Link to="/contact" className="mt-2 inline-flex items-center gap-2 bg-sage-500 hover:bg-sage-600 text-white font-semibold px-8 py-3.5 rounded-full transition-all hover:-translate-y-0.5 hover:shadow-lg">
              Get in touch <ArrowRight size={18} />
            </Link>
          </div>
          <div className="reveal rounded-3xl overflow-hidden shadow-xl">
            <img src={arranging.image} alt="Dog enjoying hydrotherapy" className="w-full h-full object-cover aspect-[9/10]" />
          </div>
        </div>
      </section>
    </div>
  );
}
